import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Users, AlertCircle } from 'lucide-react';

import { AdminLayout } from '../../components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { supabase } from '../../lib/supabase';
import { useImpersonationStore } from '../../store/impersonation.store';

interface UserResult {
  id: string | null;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
  created_at: string | null;
  global_status: string | null;
}

export default function ImpersonationPortal() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [users, setUsers] = useState<UserResult[]>([]);
  const [selectedUser, setSelectedUser] = useState<UserResult | null>(null);
  const [reason, setReason] = useState('');
  const [searching, setSearching] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;
    
    setSearching(true);
    setError(null);
    setUsers([]);
    setSelectedUser(null);
    
    try {
      const query = searchQuery.trim().toLowerCase();
      
      // Search user_profiles by email, first_name, or last_name
      const { data, error: searchError } = await supabase
        .from('impersonation_eligible_users')
        .select('id, email, first_name, last_name, created_at, global_status')
        .or(`email.ilike.%${query}%,first_name.ilike.%${query}%,last_name.ilike.%${query}%`)
        .order('created_at', { ascending: false })
        .limit(50);

      if (searchError) {
        setError(searchError.message);
        return;
      }

      // Filter out any records with null ids (shouldn't happen, but TypeScript safety)
      setUsers((data || []).filter((user): user is UserResult => user.id !== null));
      
      if (!data || data.length === 0) {
        setError('no_results');
      }
    } catch (err) {
      console.error('[ImpersonationPortal] Search error:', err);
      setError(err instanceof Error ? err.message : 'search_failed');
    } finally {
      setSearching(false);
    }
  };

  const handleStartImpersonation = async () => {
    if (!selectedUser || !reason.trim()) {
      setError('missing_required_fields');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setError('no_session');
        return;
      }

      if (!selectedUser.id) {
        setError('invalid_user_id');
        return;
      }

      const { data: newSession, error: insertError } = await supabase
        .from('impersonation_sessions')
        .insert({
          developer_id: session.user.id,
          impersonated_user_id: selectedUser.id,
          reason: reason.trim(),
          is_active: true,
        })
        .select('id, started_at')
        .single();

      if (insertError) {
        console.error('[ImpersonationPortal] Failed to create session:', insertError);
        setError(insertError.message);
        return;
      }

      console.warn('[ImpersonationPortal] Session created:', newSession.id);
      
      // Store session in Zustand store
      const { setSession } = useImpersonationStore.getState();
      setSession({
        sessionId: newSession.id,
        adminId: session.user.id,
        impersonatedUserId: selectedUser.id, // Already validated above
        impersonatedUserEmail: selectedUser.email || 'unknown',
        startedAt: newSession.started_at,
        reason: reason.trim(),
        isActive: true,
      });

      // Navigate to normal user dashboard
      navigate('/dashboard');
    } catch (err) {
      console.error('[ImpersonationPortal] Start error:', err);
      setError(err instanceof Error ? err.message : 'unknown_error');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toISOString().split('T')[0];
  };

  return (
    <AdminLayout>
      <div className="w-full max-w-[100vw] mx-auto px-4 sm:px-5 md:px-6 lg:px-7 xl:px-8 2xl:px-10 pt-2 sm:pt-3 md:pt-4 lg:pt-5 xl:pt-6 pb-8">
        <div className="mb-4 sm:mb-5 md:mb-6">
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#EBFFEE] mb-1 sm:mb-2">User Impersonation</h1>
          <p className="text-xs sm:text-sm md:text-base text-[#A9A3A3]">Search for users and start an impersonation session for support purposes</p>
        </div>

        {/* Main Content */}
        <div className="space-y-6">
          {/* Search Section */}
          <Card className="bg-[#221F1F] border-[#504A4A]">
            <CardHeader>
              <CardTitle className="text-[#EBFFEE] flex items-center gap-2">
                <Search className="w-5 h-5 text-rollops-accent" />
                User Search
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#A9A3A3] w-4 h-4" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search by email, first name, or last name..."
                    className="w-full pl-10 pr-4 py-2 bg-[#363232] border border-[#504A4A] rounded-md text-[#EBFFEE] placeholder-[#A9A3A3] focus:outline-none focus:ring-2 focus:ring-[#9B1421] text-sm sm:text-base"
                    disabled={searching || loading}
                  />
                </div>
                <button
                  type="submit"
                  disabled={searching || loading || !searchQuery.trim()}
                  className="w-full sm:w-auto px-6 py-2 bg-[#9B1421] hover:bg-[#7A0F1A] text-[#EBFFEE] rounded-md font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm sm:text-base"
                >
                  {searching ? 'Searching...' : 'Search'}
                </button>
              </form>
            </CardContent>
          </Card>

          {/* Error Display */}
          {error && (
            <Card className="bg-[#221F1F] border-[#9B1421]">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3 text-rollops-accent">
                  <AlertCircle className="w-5 h-5" />
                  <div>
                    {error === 'no_results' && 'No users found matching your search query'}
                    {error === 'missing_required_fields' && 'User selection and reason are required'}
                    {error === 'no_session' && 'Authentication session not found. Please log in again.'}
                    {error === 'invalid_user_id' && 'Invalid user ID'}
                    {error !== 'no_results' && error !== 'missing_required_fields' && error !== 'no_session' && error !== 'invalid_user_id' && error}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Results Table */}
          {users.length > 0 && (
            <Card className="bg-[#221F1F] border-[#504A4A]">
              <CardHeader>
                <CardTitle className="text-[#EBFFEE]">Search Results ({users.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-[#504A4A]">
                        <th className="text-left py-3 px-4 text-[#EBFFEE] font-medium w-8" />
                        <th className="text-left py-3 px-4 text-[#EBFFEE] font-medium">Email</th>
                        <th className="text-left py-3 px-4 text-[#EBFFEE] font-medium">Name</th>
                        <th className="text-left py-3 px-4 text-[#EBFFEE] font-medium">Status</th>
                        <th className="text-left py-3 px-4 text-[#EBFFEE] font-medium">Created</th>
                        <th className="text-left py-3 px-4 text-[#EBFFEE] font-medium">ID</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((user) => (
                        <tr
                          key={user.id || `user-${user.email || 'unknown'}`}
                          onClick={() => setSelectedUser(user)}
                          className={`
                            border-b border-[#504A4A] cursor-pointer hover:bg-[#363232]
                            ${selectedUser?.id === user.id 
                              ? 'bg-[#9B1421]/20' 
                              : ''
                            }
                          `}
                        >
                          <td className="py-4 px-4 text-center">
                            {selectedUser?.id === user.id && (
                              <div className="w-2 h-2 bg-[#9B1421] rounded-full mx-auto" />
                            )}
                          </td>
                          <td className="py-4 px-4 text-[#EBFFEE]">{user.email}</td>
                          <td className="py-4 px-4 text-[#EBFFEE]">
                            {user.first_name && user.last_name 
                              ? `${user.first_name} ${user.last_name}`
                              : <span className="text-[#A9A3A3]">N/A</span>
                            }
                          </td>
                          <td className="py-4 px-4">
                            <span className={`px-2 py-1 rounded text-xs ${
                              (user.global_status || 'active') === 'active' 
                                ? 'bg-green-500/20 text-green-400' 
                                : 'bg-gray-500/20 text-gray-400'
                            }`}>
                              {user.global_status || 'active'}
                            </span>
                          </td>
                          <td className="py-4 px-4 text-[#A9A3A3]">{formatDate(user.created_at)}</td>
                          <td className="py-4 px-4">
                            <span className="text-xs text-[#706A6A] font-mono">
                              {user.id ? `${user.id.slice(0, 8)}...` : 'N/A'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Selected User Details */}
          {selectedUser && (
            <Card className="bg-[#221F1F] border-[#504A4A]">
              <CardHeader>
                <CardTitle className="text-[#EBFFEE] flex items-center gap-2">
                  <Users className="w-5 h-5 text-rollops-accent" />
                  Selected User
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="bg-[#363232] rounded-lg p-4">
                  <table className="w-full text-sm">
                    <tbody>
                      <tr className="border-b border-[#504A4A]">
                        <td className="px-2 py-2 text-[#A9A3A3] w-32">ID</td>
                        <td className="px-2 py-2 text-[#EBFFEE] font-mono text-xs">{selectedUser.id}</td>
                      </tr>
                      <tr className="border-b border-[#504A4A]">
                        <td className="px-2 py-2 text-[#A9A3A3]">Email</td>
                        <td className="px-2 py-2 text-[#EBFFEE]">{selectedUser.email}</td>
                      </tr>
                      <tr className="border-b border-[#504A4A]">
                        <td className="px-2 py-2 text-[#A9A3A3]">First Name</td>
                        <td className="px-2 py-2 text-[#EBFFEE]">{selectedUser.first_name || 'N/A'}</td>
                      </tr>
                      <tr className="border-b border-[#504A4A]">
                        <td className="px-2 py-2 text-[#A9A3A3]">Last Name</td>
                        <td className="px-2 py-2 text-[#EBFFEE]">{selectedUser.last_name || 'N/A'}</td>
                      </tr>
                      <tr>
                        <td className="px-2 py-2 text-[#A9A3A3]">Status</td>
                        <td className="px-2 py-2">
                          <span className={`px-2 py-1 rounded text-xs ${
                            (selectedUser.global_status || 'active') === 'active' 
                              ? 'bg-green-500/20 text-green-400' 
                              : 'bg-gray-500/20 text-gray-400'
                          }`}>
                            {selectedUser.global_status || 'active'}
                          </span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Reason Input */}
          {selectedUser && (
            <Card className="bg-[#221F1F] border-[#504A4A]">
              <CardHeader>
                <CardTitle className="text-[#EBFFEE]">Impersonation Reason</CardTitle>
                <p className="text-sm text-[#A9A3A3] mt-1">Required for audit and compliance purposes</p>
              </CardHeader>
              <CardContent>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g., Troubleshooting user-reported issue #1234"
                  rows={4}
                  className="w-full bg-[#363232] border border-[#504A4A] rounded-md px-4 py-3 text-[#EBFFEE] placeholder-[#A9A3A3] focus:outline-none focus:ring-2 focus:ring-[#9B1421] resize-none"
                  disabled={loading}
                />
              </CardContent>
            </Card>
          )}

          {/* Action Buttons */}
          {selectedUser && (
            <div className="flex gap-4 justify-end">
              <button
                onClick={() => {
                  setSelectedUser(null);
                  setReason('');
                  setError(null);
                }}
                disabled={loading}
                className="px-6 py-2 bg-[#363232] hover:bg-[#504A4A] text-[#EBFFEE] rounded-md font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Clear Selection
              </button>
              <button
                onClick={handleStartImpersonation}
                disabled={loading || !reason.trim()}
                className={`
                  px-6 py-2 rounded-md font-medium transition-colors
                  ${loading || !reason.trim()
                    ? 'bg-[#363232] text-[#706A6A] cursor-not-allowed'
                    : 'bg-[#9B1421] hover:bg-[#7A0F1A] text-[#EBFFEE]'
                  }
                `}
              >
                {loading ? 'Starting Session...' : 'Start Impersonation'}
              </button>
            </div>
          )}

          {/* Info */}
          <Card className="bg-[#221F1F] border-[#504A4A]">
            <CardContent className="pt-6">
              <p className="text-sm text-[#A9A3A3]">
                All impersonation sessions are logged for compliance. Session data includes developer ID, user ID, reason, and timestamps.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
}


